import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, EventExpenseResponse, EventExpenseImageResponse } from '../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { AddExpense } from './components/add-expense/add-expense';
import { ToastService } from '../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-manage-event-expenses',
  standalone: false,
  templateUrl: './manage-event-expenses.html',
  styleUrl: './manage-event-expenses.css'
})
export class ManageEventExpenses implements OnInit {
  @Input() event!: EventResponse;

  expenses: EventExpenseResponse[] = [];
  loading: boolean = false;
  selectedUserId: string | null = null;

  // Lightbox
  lightboxImages: EventExpenseImageResponse[] = [];
  lightboxIndex: number = 0;

  constructor(
    private eventsService: EventsService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.loadExpenses();
  }

  loadExpenses() {
    this.loading = true;
    this.eventsService.getEventExpenses(this.event.event_id).subscribe({
      next: (expenses) => {
        this.expenses = expenses;
        this.loading = false;
        console.log('Expenses loaded:', expenses);
        console.log('Unique users:', this.uniqueUsers);
      },
      error: () => {
        this.loading = false;
        this.expenses = [];
      }
    });
  }

  get uniqueUsers(): { user_id: string; full_name: string }[] {
    const userMap = new Map<string, string>();
    this.expenses.forEach(exp => {
      if (exp.user && exp.user.user_id) {
        userMap.set(exp.user.user_id, exp.user.full_name || `${exp.user.first_name} ${exp.user.last_name}`);
      }
    });
    return Array.from(userMap.entries()).map(([user_id, full_name]) => ({ user_id, full_name }));
  }

  get filteredExpenses(): EventExpenseResponse[] {
    if (!this.selectedUserId) {
      return this.expenses;
    }
    return this.expenses.filter(exp => exp.user?.user_id === this.selectedUserId);
  }

  get hasFilter(): boolean {
    return this.selectedUserId !== null;
  }

  getTotalExpenses(): number {
    return this.filteredExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  }

  onUserFilterChange(userId: string | null) {
    console.log('Filter changed:', userId);
    console.log('Filtered expenses:', this.filteredExpenses.length);
    this.selectedUserId = userId;
  }

  clearFilter() {
    this.selectedUserId = null;
  }

  openAddModal() {
    const modalRef = this.modalService.open(AddExpense, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((result) => {
      if (result) {
        this.loadExpenses();
      }
    }, () => {});
  }

  openEditModal(expense: EventExpenseResponse) {
    const modalRef = this.modalService.open(AddExpense, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.expense = expense;
    modalRef.componentInstance.isEdit = true;
    modalRef.result.then((result) => {
      if (result) {
        this.loadExpenses();
      }
    }, () => {});
  }

  deleteExpense(expense: EventExpenseResponse) {
    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.expenses.deleteConfirm', { title: expense.title });
    modalRef.result.then((result) => {
      if (result) {
        this.eventsService.deleteEventExpense(expense.expense_id).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.expenses.deleteSuccess'));
            this.loadExpenses();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.expenses.deleteError'));
          }
        });
      }
    }, () => {});
  }

  getDefaultCurrency(): string {
    return this.event?.default_currency || 'NOK';
  }

  formatCurrency(amount: number, currency?: string): string {
    const currencyCode = currency || this.getDefaultCurrency();
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  openReceiptsLightbox(images: EventExpenseImageResponse[], startIndex: number = 0) {
    this.lightboxImages = images;
    this.lightboxIndex = startIndex;
  }

  closeLightbox() {
    this.lightboxImages = [];
    this.lightboxIndex = 0;
  }

  nextImage() {
    if (this.lightboxIndex < this.lightboxImages.length - 1) {
      this.lightboxIndex++;
    }
  }

  prevImage() {
    if (this.lightboxIndex > 0) {
      this.lightboxIndex--;
    }
  }

  exportToPDF() {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Expense Report', 14, 22);

    // Event name
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Event: ${this.event.event_name}`, 14, 32);

    // Date
    const today = new Date().toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(`Generated: ${today}`, 14, 40);

    // Filter info
    if (this.hasFilter) {
      const selectedUser = this.uniqueUsers.find(u => u.user_id === this.selectedUserId);
      doc.text(`Filtered by: ${selectedUser?.full_name || 'Unknown'}`, 14, 48);
    }

    // Table data
    const tableData = this.filteredExpenses.map(expense => [
      expense.title,
      expense.description || '-',
      expense.user?.full_name || '-',
      this.formatDate(expense.created_at),
      this.formatCurrency(expense.amount, expense.currency)
    ]);

    // Add table
    autoTable(doc, {
      startY: this.hasFilter ? 55 : 48,
      head: [['Title', 'Description', 'Added By', 'Date', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [102, 126, 234],
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 10,
        cellPadding: 3
      },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { cellWidth: 50 },
        4: { halign: 'right', fontStyle: 'bold' }
      }
    });

    // Total row
    const finalY = (doc as any).lastAutoTable.finalY || 100;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total:', 14, finalY + 10);
    doc.text(this.formatCurrency(this.getTotalExpenses()), 196, finalY + 10, { align: 'right' });

    // Summary
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${this.filteredExpenses.length} expense(s) recorded`, 14, finalY + 18);

    // Save
    const fileName = `expenses-${this.event.event_name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }
}
